import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import axios from "axios";
import spinner from "../layout/spinner.gif";
import { getTeamPhotos, reviewTeamPhoto } from "../../actions/bundles";
import { primaryButton, secondaryButton, smallButton } from "../template/templateLabels";
import { card } from "../ads/adLabels";

// The teams' story photos (the backend's routes/api/teamPhotos.js). cloudprint.mn/stories
// shows one once everyone in its team has agreed and an admin let it in here; a team
// member who takes their consent back hides it at once, without an admin.

const TABS = [
	{
		status: "review",
		label: "Шалгах",
		note: "Багийн бүх гишүүн зөвшөөрсөн. Зургийг томоор нь нээж, зөвхөн багийнхан байгаа эсэхийг шалгаад нийтлэнэ үү.",
	},
	{ status: "public", label: "Нийтлэгдсэн", note: "cloudprint.mn/stories дээр харагдаж байна." },
	{ status: "waiting", label: "Зөвшөөрөл хүлээж буй", note: "Багийн зарим гишүүн хариулаагүй байна." },
	{ status: "declined", label: "Татгалзсан", note: "Гишүүдийн хэн нэг нь зөвшөөрөөгүй тул харагдахгүй." },
	{ status: "rejected", label: "Нийтлэхгүй", note: "Admin нийтлэхгүй гэж шийдсэн." },
];

const dayText = (date) => (date ? new Date(new Date(date).getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10) : "—");

// A picture only admins and the team may see: fetched with the token
const useImage = (url) => {
	const [src, setSrc] = useState(null);
	useEffect(() => {
		let live = true;
		let objectUrl = null;
		axios
			.get(url, { responseType: "blob" })
			.then((res) => {
				objectUrl = URL.createObjectURL(res.data);
				if (live) setSrc(objectUrl);
				else URL.revokeObjectURL(objectUrl);
			})
			.catch(() => {});
		return () => {
			live = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [url]);
	return src;
};

const openLarge = async (url) => {
	// Opened before the picture is here, so the browser does not block it
	const tab = window.open("", "_blank");
	try {
		const res = await axios.get(url, { responseType: "blob" });
		if (tab) tab.location.href = URL.createObjectURL(res.data);
	} catch (err) {
		if (tab) tab.close();
	}
};

const Photo = ({ photo, onReview }) => {
	const src = useImage(photo.thumb);
	const [busy, setBusy] = useState(false);
	const review = async (approve) => {
		setBusy(true);
		await onReview(photo, approve);
		setBusy(false);
	};
	return (
		<li className={"flex flex-col overflow-hidden " + card}>
			<button
				type="button"
				className="block w-full bg-gray-100 dark:bg-gray-700"
				style={{ aspectRatio: "4 / 5" }}
				onClick={() => openLarge(photo.web)}
				title="Томоор харах"
			>
				{src && <img src={src} alt={photo.caption} className="object-cover w-full h-full" />}
			</button>
			<div className="flex flex-col flex-1 gap-1 p-3 text-sm">
				<p className="font-semibold text-gray-800 dark:text-gray-100">{photo.caption || "Тайлбаргүй"}</p>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					{photo.bundle.name}
					{photo.bundle.season ? " · " + photo.bundle.season.label : ""}
				</p>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					{photo.owner.name} · {photo.owner.email}
				</p>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					{photo.agreed}/{photo.people} зөвшөөрсөн
					{photo.declined > 0 ? ` · ${photo.declined} татгалзсан` : ""} · {dayText(photo.date)}
				</p>
				<div className="flex flex-wrap gap-2 pt-2 mt-auto">
					{(photo.status === "review" || photo.status === "rejected") && (
						<button type="button" className={primaryButton} disabled={busy} onClick={() => review(true)}>
							Нийтлэх
						</button>
					)}
					{photo.status === "review" && (
						<button type="button" className={secondaryButton} disabled={busy} onClick={() => review(false)}>
							Нийтлэхгүй
						</button>
					)}
					{photo.status === "public" && (
						<button type="button" className={smallButton} disabled={busy} onClick={() => review(false)}>
							Нуух
						</button>
					)}
				</div>
			</div>
		</li>
	);
};

const TeamStories = ({ getTeamPhotos, reviewTeamPhoto }) => {
	const [status, setStatus] = useState("review");
	const [data, setData] = useState(null);

	const load = useCallback(async () => {
		setData(null);
		setData(await getTeamPhotos(status));
	}, [getTeamPhotos, status]);
	useEffect(() => {
		load();
	}, [load]);

	const review = async (photo, approve) => {
		if (!approve && photo.status === "public" && !window.confirm("Энэ зургийг түүхээс нуух уу?")) return;
		if (await reviewTeamPhoto(photo.id, approve)) load();
	};

	const tab = TABS.find((one) => one.status === status);

	return (
		<section className={"mb-6 overflow-hidden " + card}>
			<div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
				<h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Багуудын түүх</h3>
				<p className="text-sm text-gray-500 dark:text-gray-400">
					Багийн бүх гишүүн зөвшөөрч, энд «Нийтлэх» дарсан зураг л cloudprint.mn/stories дээр гарна. Зурган
					дээр багийнхнаас өөр хүн, хувийн мэдээлэл, зохисгүй зүйл байвал нийтлэхгүй.
				</p>
				<div className="flex flex-wrap gap-2 mt-3">
					{TABS.map((one) => (
						<button
							key={one.status}
							type="button"
							onClick={() => setStatus(one.status)}
							className={
								"px-3 py-1 text-xs font-semibold rounded-full " +
								(one.status === status
									? "text-white bg-purple-600"
									: "text-gray-600 bg-gray-100 dark:text-gray-300 dark:bg-gray-700")
							}
						>
							{one.label}
							{data && data.counts && data.counts[one.status] > 0 ? ` (${data.counts[one.status]})` : ""}
						</button>
					))}
				</div>
			</div>
			{!data ? (
				<img src={spinner} alt="" className="w-10 mx-auto my-6" />
			) : data.photos.length === 0 ? (
				<p className="px-5 py-4 text-sm text-gray-500 dark:text-gray-400">Зураг алга. {tab.note}</p>
			) : (
				<>
					<p className="px-5 pt-4 text-xs text-gray-500 dark:text-gray-400">{tab.note}</p>
					<ul className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
						{data.photos.map((photo) => (
							<Photo key={photo.id} photo={photo} onReview={review} />
						))}
					</ul>
				</>
			)}
		</section>
	);
};

TeamStories.propTypes = {
	getTeamPhotos: PropTypes.func.isRequired,
	reviewTeamPhoto: PropTypes.func.isRequired,
};

export default connect(null, { getTeamPhotos, reviewTeamPhoto })(TeamStories);
